import {
  type ClientContextVariable,
  type SpecificValue,
  SuggestedValuesType,
} from 'common/types/clientContextVariable';
import {
  type DataSourceCCVs,
  type DataSourceParameter,
  type ParameterConfiguration,
} from 'common/types/reportFilters';
import unionWith from 'lodash/fp/unionWith';

/**
 * Given DataSourceParameter, find the corresponding client context variable
 *
 * @param data data source parameter
 * @param clientContextVariables client context variables
 *
 * @returns first matching CCV that matches the given parameter's name and uid. Undefined otherwise
 */
export function findCcvForParameter(
  { name, datasetUid }: DataSourceParameter,
  clientContextVariables?: DataSourceCCVs,
): ClientContextVariable | undefined {
  return clientContextVariables?.[datasetUid]?.find((ccv) => ccv.name === name);
}

/**
 * Look at the client context variable for the parameter and each of its linked parameters and
 * determine if it has specific values. If at least one parameter has specific values, return true.
 *
 * @param paramIds data source parameters
 * @param clientContextVariables CCVs
 *
 * @returns true if one variable is `SuggestedValuesType.SPECIFIC_VALUES`
 */
export function parameterHasSpecificValues(
  paramIds: DataSourceParameter[],
  clientContextVariables?: DataSourceCCVs,
): boolean {
  return paramIds.some((param) => {
    const ccv = findCcvForParameter(param, clientContextVariables);

    return ccv?.suggestedValuesType === SuggestedValuesType.SPECIFIC_VALUES;
  });
}

/**
 * Gets all the specific values among all params and uuids int he given param ids.
 * They are order in the order of appears by parameters given and their order in
 * matching `clientContextVariables`.
 *
 * Any subsequent values with the same `value`, will be removed.
 *
 * @param paramIds data source parameters
 * @param clientContextVariables CCVs
 *
 * @returns all the specific values
 */
export function getAllSpecificValues(
  paramIds: DataSourceParameter[],
  clientContextVariables?: DataSourceCCVs,
) {
  return paramIds.reduce((acc, param) => {
    const ccv = findCcvForParameter(param, clientContextVariables);

    return unionWith((a, b) => a.value === b.value, acc, ccv?.suggestedValues?.valueList);
  }, [] as SpecificValue[]);
}

/**
 * For parameters with specific values, we want to use the override's display name which is found in the
 * client context variable. Look through the list of linked parameters and find its client context variable.
 * The display name is in the suggested values list. If it doesn't exist, then just use the value.
 *
 * @param parameter Parameter Configuration
 * @param clientContextVariables CCV
 *
 * @returns the CCV override of the override value given in the parameters config
 */
export function getParameterWithSpecificValuesHumanText(
  { paramIds, overrideValue }: ParameterConfiguration,
  clientContextVariables?: DataSourceCCVs,
): string {
  const suggestedValue = paramIds
    .map((p) => findCcvForParameter(p, clientContextVariables)) // find all the corresponding ccv params
    .filter((ccv) => !!ccv) // filter only on ccvs that exist
    .filter(({ suggestedValuesType }) => suggestedValuesType === SuggestedValuesType.SPECIFIC_VALUES) // get only the specific values ones
    .flatMap(({ suggestedValues }) => suggestedValues?.valueList ?? []) // combine all the value lists together
    .find((sv) => sv?.value === overrideValue); // find the first where value matches

  const humanText = suggestedValue?.displayName ?? suggestedValue?.value ?? overrideValue;

  return humanText;
}

/**
 * Given a parameter, find its client context variable's default value.
 * If in view mode, the client context variable's override value is the default.
 *
 *
 * @param parameter Parameter Configuration
 * @param clientContextVariables CCV
 * @param editMode whether we are currently editing
 *
 * @returns get the corresponding CCV's default value
 */
export const findClientContextVariableDefault = (
  paramIds: DataSourceParameter[],
  clientContextVariables?: DataSourceCCVs,
  editMode: boolean = false,
): string => {
  /**
   * Notice that we're intentionally passing in `paramIds[0]`, which can be undefined
   * if `paramIds` is empty. At the time of this writing, was the existing behavior
   * (just refactored a bit).
   */
  const ccv = findCcvForParameter(paramIds[0], clientContextVariables);

  if (!ccv) {
    return '';
  }

  if (editMode) {
    return ccv.defaultValue;
  }

  return ccv.overrideValue ?? ccv.defaultValue;
};
